import { useEffect, useState } from 'react';
import { X, ExternalLink, Gift, Star } from 'lucide-react';

const STORAGE_KEY = 'promoModalDismissed';
// Reappearance interval (default 1 day). Can be configured with env vars:
// - VITE_PROMO_REAPPEAR_DAYS (number of days)
// - VITE_PROMO_REAPPEAR_MS (milliseconds)
const DEFAULT_REAPPEAR_MS = 24 * 60 * 60 * 1000;
const REAPPEAR_MS = (() => {
  try {
    const days = import.meta.env.VITE_PROMO_REAPPEAR_DAYS;
    if (days) {
      const n = Number(days);
      if (!Number.isNaN(n) && n >= 0) return n * 24 * 60 * 60 * 1000;
    }
    const ms = import.meta.env.VITE_PROMO_REAPPEAR_MS;
    if (ms) {
      const m = Number(ms);
      if (!Number.isNaN(m) && m >= 0) return m;
    }
  } catch {}
  return DEFAULT_REAPPEAR_MS;
})();

export const PromoBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const enabled = import.meta.env.VITE_PROMO_ENABLED;
      // If feature disabled explicitly, never show
      if (enabled === 'false') return;

      if (!raw) {
        setVisible(true);
        return;
      }

      const ts = Number(raw);
      if (Number.isNaN(ts)) {
        // legacy value or corrupt — show and overwrite on dismiss
        setVisible(true);
        return;
      }

      // If stored timestamp is older than REAPPEAR_MS, show again
      if (Date.now() - ts >= REAPPEAR_MS) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try {
      // store the current time so modal can reappear after REAPPEAR_MS
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      // track dismiss
      const g = (window as any).gtag;
      if (typeof g === 'function') {
        g('event', 'promo_dismiss', { method: 'modal' });
      }
    } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    // overlay starts below the fixed header so header stays visible
    <div className="fixed inset-0 z-50 pt-16 pointer-events-none">
      {/* backdrop (below header) */}
      <div className="absolute inset-0 top-16 bg-black/40 backdrop-blur-sm pointer-events-auto" />

      <div className="relative z-10 flex items-start justify-center min-h-screen pt-20 px-4 sm:px-6">
        <div className="w-full max-w-2xl pointer-events-auto">
          <div className="relative bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 transform transition-all duration-300 animate-slide-up">
            {/* left-side red close button */}
            <button
              onClick={dismiss}
              aria-label="Close promo"
              className="absolute left-[-22px] top-4 z-20 -ml-6 bg-red-600 text-white rounded-full p-2 shadow-lg hover:bg-red-700 focus:outline-none"
              style={{ transform: 'translateX(-50%)' }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg p-3 shadow-md">
                  <Gift className="w-6 h-6 text-white" />
                </div>

                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Exclusive Giveaways & Premium Offers</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">Join our Telegram channel for giveaways, premium resources, and special deals. Open the channel in a new tab to learn more.</p>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <a
                      href="https://t.me/CanvaProInviteLinks"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition transform hover:scale-105"
                      onClick={() => {
                        try {
                          const g = (window as any).gtag;
                          if (typeof g === 'function') {
                            g('event', 'promo_click', { method: 'telegram', label: 'CanvaProInviteLinks' });
                          }
                        } catch {}
                      }}
                    >
                      <Star className="w-4 h-4" />
                      Visit Telegram Channel
                      <ExternalLink className="w-4 h-4" />
                    </a>

                    <button
                      onClick={dismiss}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
